<template>
  <div class="attendance-sticky-header surface-0 mb-2">
      <div class="attendance-header-summary flex align-items-center">
        <div class="px-0 py-2">
          <h2 class="m-0 p-0 font-light text-600 text-1xl">{{ pageTitle }}</h2>
          <p v-if="hasSelectedRows" class="selection-summary p-0 m-0 pt-1 text-xs text-500 font-semibold">
            {{ selectionSummary }}
          </p>
          <p v-else class="p-0 m-0 pt-1 text-xs text-400">
            Showing {{ allAttendance.length }} of {{ attendancePagination.total_items }} results.
          </p>
        </div>
      </div>
      <div class="attendance-filters">
        <div v-if="hasSelectedRows" style="position: fixed; right: 30px; bottom: 30px; z-index: 50;" class="shadow-8 m-3">
          <Button v-if="hasSingleSelectedRow" @click="openAttendanceEditor" label="Edit Attendance" icon="pi pi-pencil" class="px-4 py-3 text-lg w-full block shadow-3 surface-300 hover:surface-500 mb-2" />
          <Button v-if="hasMultipleSelectedRows && authStore.isSuperAdmin" @click="openAttendanceBulkEditor" label="Bulk Attendance Edit" icon="pi pi-pencil" class="px-4 py-3 text-lg w-full block shadow-3 surface-300 hover:surface-500 mb-2" />
          <Button v-if="hasMultipleSelectedRows" @click="openAttendanceCompanyEditor" label="Change Company" icon="pi pi-building" class="px-4 py-3 text-lg w-full block shadow-3 surface-300 hover:surface-500 w-full" />
        </div>
        <Select v-model="selectedCompanyId"
                :options="companiesList"
                optionLabel="company_name"
                optionValue="company_id"
                filter
                showClear
                size="small"
                filterPlaceholder="Search companies"
                :loading="selectionLoading"
                :disabled="isRefreshing || selectionLoading"
                placeholder="All Companies"
                class="w-full md:w-13rem"
                @change="handleCompanyChange" />
        <MultiSelect v-model="selectedEmployeeIds"
                    :options="employeeOptions"
                    optionLabel="employee_name"
                    optionValue="employee_id"
                    filter
                    size="small"
                    filterPlaceholder="Search employees"
                    :loading="selectionLoading"
                    :disabled="isRefreshing || selectionLoading"
                    placeholder="All Employees"
                    class="w-full md:w-18rem">
          <template #value>
            <span>{{ employeeSelectionLabel }}</span>
          </template>
        </MultiSelect>
        <Calendar v-model="dateRange" 
                  selectionMode="range" 
                  :showIcon="true" 
                  :manualInput="false"
                  size="small"
                  :disabled="isRefreshing"
                  dateFormat="yy-mm-dd"
                  placeholder="Select Date Range" 
                  class="w-full md:w-15rem"
                  :hideOnRangeSelection="true"
                  @date-select="handleDateSelect" />
        <Dropdown v-model="selectedType" size="small" :options="typeList" :disabled="isRefreshing" optionLabel="name" placeholder="Type" class="w-full md:w-11rem">
          <template #value="slotProps">
              <div v-if="slotProps.value" class="flex align-items-center">
                  {{ slotProps.value.name }}
              </div>
              <span v-else class="text-300">
                  {{ slotProps.placeholder }}
              </span>
          </template>
        </Dropdown>
        <Dropdown v-model="selectedShiftType" size="small" :options="shiftTypeList" :disabled="isRefreshing" optionLabel="name" placeholder="Attendance Type" class="w-full md:w-10rem">
          <template #value="slotProps">
              <div v-if="slotProps.value" class="flex align-items-center">
                  {{ slotProps.value.name }}
              </div>
              <span v-else class="text-300">
                  {{ slotProps.placeholder }}
              </span>
          </template>
        </Dropdown>
        <label for="show-not-logout" class="not-logout-filter flex align-items-center gap-2 cursor-pointer">
          <Checkbox v-model="showNotLogout" inputId="show-not-logout" binary :disabled="isRefreshing" />
          <span>Show not logout shifts</span>
        </label>
        <Button icon="pi pi-search"
                label="Apply"
                size="small"
                aria-label="Apply attendance filters"
                v-tooltip.top="'Apply filters'"
                :loading="isRefreshing"
                :disabled="!hasCompleteDateRange || selectionLoading"
                @click="handleSearchClick" />
    </div>
  </div>
  <div v-if="attendanceLoading || attendancePagination.total_items">
    <DataTable :value="allAttendance"
               selectionMode="multiple"
               v-model:selection="selectedRows"
               dataKey="attendance_id"
               :metaKeySelection="false"
               lazy
               size="small"
               sortMode="single"
               tableStyle="min-height: 20rem"
               :sortField="sortField"
               :sortOrder="sortOrder"
               :loading="attendanceLoading || isRefreshing"
               @sort="onSort">
      <Column selectionMode="multiple" style="width: 1rem;"></Column>
      <Column field="employee_name" header="Employee" class="text-600 w-3">
          <template #body="slotProps">
              <div class="flex gap-2">
                  <img alt="flag" :src="slotProps.data.employee_photo_url || defaultUserImage" class="max-w-3rem max-h-3rem employee-photo" />
                  <div>
                    <span class="block font-bold">{{ slotProps.data.employee_name }}</span>
                    <span class="block text-xs text-300">{{ slotProps.data.employee_company_name }}</span>
                    <span class="text-xs text-300 mr-2">
                      <span class="mr-2">@{{ slotProps.data.employee_id }}</span>
                      <span class="mr-2">{{ slotProps.data.employee_status }}</span>
                      <span class="mr-2">{{ slotProps.data.employee_department }}</span>
                    </span>
                  </div>
              </div>
          </template>
          <template #footer>
            <div class="flex gap-2">
                <div class="flex surface-100 align-items-center justify-content-center border-round p-2">
                    <span class="text-xs font-light">Records: <span class="font-bold">{{ allAttendance.length }} / {{ attendancePagination.total_items }}</span></span>
                </div>
            </div>
          </template>
      </Column>
      <Column field="shift_in_day" header="Date" class="text-600 w-1">
        <template #body="slotProps">
            <div class="gap-2 line-height-3">
                  <span class="block">{{ slotProps.data.shift_in_day }}</span>
                  <span class="block text-xs text-300">{{ slotProps.data.shift_in_month }}</span>
            </div>
        </template>
      </Column>
      <Column field="shift_in" sortField="shift_in_unix" header="Roster" sortable class="text-600 w-2">
        <template #body="slotProps">
          <div class="flex gap-2">
              <div class="gap-2 line-height-3">
                    <span class="block flex"><span>{{ slotProps.data.shift_in }}</span></span>
                    <span class="block flex"><span>{{ slotProps.data.shift_out }}</span></span>
              </div>
            <div class="flex surface-100 align-items-center justify-content-center border-round p-2">
              <span class="text-400">{{ calculateRowDurations(slotProps.data).shiftDuration }}</span> 
            </div>
          </div>
        </template>
        <template #footer>
          <div class="flex gap-2">
            <div class="flex surface-100 align-items-center justify-content-center border-round p-2">
              <span class="text-xs font-light">Roster: <span class="font-bold">{{ formatMinutesToTime(totalDuration) }}</span></span>
            </div>
          </div>
        </template>
      </Column>
      <Column field="attendance_checkin" sortField="attendance_checkin_unix" header="Attendance" sortable class="text-600 w-3">
        <template #body="slotProps">
          <div class="flex gap-2">
            <div class="gap-2 line-height-3">
              <span class="block flex"><span>{{ slotProps.data.attendance_checkin }}</span></span>
              <span v-if="slotProps.data.attendance_checkout_unix > 0" class="block flex"><span>{{ slotProps.data.attendance_checkout }}</span></span>
              <span v-else-if="slotProps.data.shift_out_unix < generateCurrentTimeunix" class="block flex text-yellow-800 uppercase text-xs" 
                    style="letter-spacing: 2px;"><span>Not Logout</span>
              </span>
              <span v-else class="block flex text-300 uppercase text-xs" style="letter-spacing: 2px;"><span>Working</span></span>
            </div>
            <div v-if="slotProps.data.attendance_checkout_unix > 0" class="flex surface-100 align-items-center justify-content-center border-round p-2 w-3rem">
              <span class="text-400">{{ calculateRowDurations(slotProps.data).attendanceDuration }}</span>
            </div>
            <div v-if="slotProps.data.attendance_checkout_unix > 0" :class="{
                'bg-green-900': (slotProps.data.attendance_status_out==='OVERTIME' && slotProps.data.over_below_status === true) ||
                (slotProps.data.attendance_status_in==='EARLY' && slotProps.data.over_below_status === true), 
                'bg-red-800': (
                    (slotProps.data.attendance_status_out==='OVERTIME' && slotProps.data.over_below_status !== true) ||
                    (slotProps.data.attendance_status_in==='EARLY' && slotProps.data.over_below_status !== true) ||
                    !calculateRowDurations(slotProps.data).netWorkedHours ||
                    calculateRowDurations(slotProps.data).netWorkedHours === '0:00' ||
                    calculateRowDurations(slotProps.data).netWorkedHours === 'ERROR'
                ), 
                'flex surface-100 align-items-center justify-content-center border-round p-2 w-3rem': true
              }">
              <span 
                class="text-400 font-bold" 
                :class="
                  slotProps.data.over_below_status !== true
                  && slotProps.data.employee_company_id !==1
                  && (slotProps.data.attendance_status_out==='OVERTIME'
                  || slotProps.data.attendance_status_in==='EARLY')
                  ? 'flash-in-out' : 'relative'">
                {{ calculateRowDurations(slotProps.data).netWorkedHours }}
              </span>
            </div>
          </div>
        </template>
        <template #footer>
          <div class="flex gap-2">
            <div class="flex surface-100 align-items-center justify-content-center border-round p-2">
              <span class="text-xs font-light">Worked: <span class="font-bold">{{ formatMinutesToTime(totalWorkedHours) }}</span></span>
            </div>
            <div class="flex surface-100 align-items-center justify-content-center border-round p-2">
              <span class="text-xs font-light">Net: <span class="font-bold">{{ formatMinutesToTime(totalNetWorkedHours) }}</span></span>
            </div>
          </div>
        </template>
      </Column>
      <Column field="attendance_status_in" header="Status" class="text-600 w-1">
        <template #body="slotProps">
            <div class="gap-2 text-center">
              <Tag severity="info" :class="
                  {'block bg-yellow-800 mb-1 p-0 m-0 border-round-sm': slotProps.data.attendance_status_in==='LATE', 
                  'block bg-cyan-800 mb-1 p-0 m-0 border-round-sm': slotProps.data.attendance_status_in==='EARLY', 
                  'block surface-300 mb-1 p-0 m-0 border-round-sm': slotProps.data.attendance_status_in==='ONTIME'}" style="letter-spacing: 2px;">
                  {{ slotProps.data.attendance_status_in }}
              </Tag>
              <Tag severity="info" :class="
                  {'block bg-green-800 mb-1 p-0 m-0 border-round-sm': slotProps.data.attendance_status_out==='OVERTIME', 
                  'block bg-red-800 mb-1 p-0 m-0 border-round-sm': slotProps.data.attendance_status_out==='EARLY', 
                  'block surface-100 text-300 mb-1 text-xs p-0 m-0 border-round-sm': slotProps.data.attendance_status_out==='NOT', 
                  'block surface-300 mb-1 p-0 m-0 border-round-sm': slotProps.data.attendance_status_out==='ONTIME'}" style="letter-spacing: 2px;">
                  {{ slotProps.data.attendance_status_out }}
              </Tag>
              <Tag v-if="slotProps.data.shift_type==='COVER'" severity="info" :class="
                  {'block bg-teal-800 p-0 m-0 border-round-sm': slotProps.data.shift_type==='COVER'}" style="letter-spacing: 2px;">
                  {{ slotProps.data.shift_type }}
              </Tag>
            </div>
        </template>
      </Column>
      <Column field="attendance_logs" header="Comments" class="text-600 w-2">
          <template #body="slotProps">
            <div v-if="slotProps.data.attendance_logs && slotProps.data.attendance_logs.length > 0">
              <div v-for="(log, index) in formatLogs(slotProps.data.attendance_logs)" :key="index" class="mb-1">
                <span :class="{
                  'text-yellow-200': log.priority==='MEDIUM', 
                  'text-red-400': log.priority==='HIGH', 
                  'text-xs text-400 mr-2': true}">
                  @{{ log.username }}
                </span>
                <span :class="{
                  'text-yellow-200': log.priority==='MEDIUM', 
                  'text-red-400': log.priority==='HIGH', 
                  'text-xs text-400 mr-2': true}">
                  {{ log.message }}
                </span>
              </div>
            </div>
            <div v-else class="text-xs text-200">No comments</div>
          </template>
        </Column>
        <template #loading>
          <div class="text-400 py-20">Loading Attendance...</div>
        </template>
    </DataTable>
    <Paginator class="mt-2" :rows="PAGE_SIZE" :totalRecords="attendancePagination.total_items" :first="first" @page="onPage" />
    </div>
    <div v-else class="py-2 text-400">
      No Attendance found for this filter.
    </div>

</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAttendance } from "@/composables/api/useAttendance";
import { useToast } from 'primevue/usetoast';
import { useUtils } from '@/composables/useUtils';
import { useLondonUnixTime } from '@/composables/time/useLondonTime';
import { useCompanyEmployeeSelection } from '@/composables/useCompanyEmployeeSelection';
import { useAuthStore } from '@/stores/auth';
import {
  buildAttendanceRoute,
  buildDefaultAttendanceRoute,
  parseAttendanceRouteFilters,
  parseAttendanceSortQuery
} from '@/composables/useAttendanceRouteFilters';
import {
  formatCalendarDate
} from '@/composables/useRosterRouteFilters';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import Paginator from 'primevue/paginator';
import defaultUserImage from '@/assets/default-user.png';
import Calendar from 'primevue/calendar';

const PAGE_SIZE = 100;
const SCHEDULE_TIMEZONE = 'Europe/London';
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

// API: Attendance  
const {
  allAttendance,
  attendanceLoading,
  attendanceError,
  attendancePagination,
  attendanceSummary,
  searchAttendance
} = useAttendance();

// Utils: Calculate Duration and Net Worked Hours
const { calculateDuration, calculateNetWorkedHours } = useUtils();
const { generateCurrentTimeunix } = useLondonUnixTime();

const {
  companiesList,
  eligibleEmployees,
  employeeOptions,
  selectedCompanyId,
  selectedEmployeeIds,
  selectedEmployeeFirstNames,
  selectionLoading,
  handleCompanyChange,
  setRouteSelection,
  ensureSelectionData
} = useCompanyEmployeeSelection();

// Toast: Display Messages
const toast = useToast();

// State: Selected Rows, Sorting, Pagination, Refreshing
const selectedRows = ref([]);
const currentPage = ref(1);
const sortField = ref('attendance_checkin_unix');
const sortOrder = ref(-1);
const isRefreshing = ref(false);
const appliedCompanyId = ref(null);
const appliedEmployeeIds = ref([]);
const routeInitialized = ref(false);
const refreshQueued = ref(false);
const first = computed(() => (currentPage.value - 1) * PAGE_SIZE);

const getSortValues = () => {
  const attendanceSort = sortField.value === 'attendance_checkin_unix';
  return {
    sort: attendanceSort ? 'attendance' : 'roster',
    sortCode: attendanceSort ? 'ATTENDANCE_START' : 'ROSTER_START',
    order: sortOrder.value === -1 ? 'desc' : 'asc',
    orderCode: sortOrder.value === -1 ? 'DESC' : 'ASC'
  };
};

// Computed: Type List for Filtering
const typeList = [
  { name: 'All', code: 'ALL' },
  { name: 'Late', code: 'LATE' },
  { name: 'Early', code: 'EARLY' },
  { name: 'Overtime', code: 'OVERTIME' },
  { name: 'Not Approved', code: 'NOTAPPROVED' }
];
const selectedType = ref(typeList[0]);
const shiftTypeList = [
  { name: 'All Types', code: 'ALL' },
  { name: 'Shift', code: 'SHIFT' },
  { name: 'Cover', code: 'COVER' }
];
const selectedShiftType = ref(shiftTypeList[0]);
const showNotLogout = ref(false);

// Function: Extract Username from Email
const extractUsername = (email) => {
  return email.split('@')[0];
};

// Computed: Format Logs for Display
const formatLogs = computed(() => (logs) => {
  return logs.map(log => ({
    ...log,
    username: extractUsername(log.user)
  }));
});

// State: Date Range for Filtering
const dateRange = ref(null);

const getDateRangeParams = () => {
  const params = {
    companyId: selectedCompanyId.value,
    employeeIds: [...selectedEmployeeIds.value],
    startDate: null,
    endDate: null
  };

  if (dateRange.value && dateRange.value[0] && dateRange.value[1]) {
    params.startDate = formatCalendarDate(dateRange.value[0]);
    params.endDate = formatCalendarDate(dateRange.value[1]);
  }

  return params;
};

const hasCompleteDateRange = computed(() => (
  Array.isArray(dateRange.value) && dateRange.value[0] && dateRange.value[1]
));

const employeeSelectionLabel = computed(() => {
  if (selectedEmployeeIds.value.length === 0) return 'All Employees';
  if (selectedEmployeeIds.value.length === 1) {
    return eligibleEmployees.value.find(
      employee => employee.employee_id === selectedEmployeeIds.value[0]
    )?.employee_name || '1 Employee selected';
  }
  return selectedEmployeeFirstNames.value.join(', ');
});

const pageTitle = computed(() => {
  if (appliedEmployeeIds.value.length === 0 && appliedCompanyId.value === null) {
    return 'All Attendance';
  }
  if (appliedEmployeeIds.value.length === 1) {
    const employeeName = eligibleEmployees.value.find(
      employee => employee.employee_id === appliedEmployeeIds.value[0]
    )?.employee_name;
    return `${employeeName || '1 Employee'} Attendance`;
  }
  if (appliedEmployeeIds.value.length === 0) {
    const companyName = companiesList.value.find(
      company => company.company_id === appliedCompanyId.value
    )?.company_name;
    return `${companyName || 'Company'} Attendance`;
  }
  return `${appliedEmployeeIds.value.length} Employees Attendance`;
});

const refreshData = async () => {
  if (isRefreshing.value) {
    refreshQueued.value = true;
    return false;
  }

  const { companyId, employeeIds, startDate, endDate } = getDateRangeParams();
  if (!startDate || !endDate) {
    handleError('Please select a complete date range.', 'Fetching Attendance');
    return false;
  }

  isRefreshing.value = true;
  try {
    selectedRows.value = [];
    const succeeded = await searchAttendance({
      companyId,
      employeeIds,
      attendanceType: selectedType.value.code,
      shiftType: selectedShiftType.value.code,
      showNotLogout: showNotLogout.value,
      startDate,
      endDate,
      timezone: SCHEDULE_TIMEZONE,
      page: currentPage.value,
      pageSize: PAGE_SIZE,
      sortField: getSortValues().sortCode,
      sortOrder: getSortValues().orderCode
    });
    if (succeeded) {
      appliedCompanyId.value = companyId;
      appliedEmployeeIds.value = [...employeeIds];
    }
    return succeeded;
  } catch (err) {
    handleError(err, 'Fetching Attendance');
    return false;
  } finally {
    isRefreshing.value = false;
    if (refreshQueued.value) {
      refreshQueued.value = false;
      await refreshData();
    }
  }
};

const handleDateSelect = () => {
  currentPage.value = 1;
};

const handleSearchClick = async () => {
  const { companyId, employeeIds, startDate, endDate } = getDateRangeParams();
  if (!startDate || !endDate) {
    handleError('Please select a complete date range.', 'Applying Attendance Filters');
    return;
  }

  const targetRoute = buildAttendanceRoute({
    companyId,
    employeeIds,
    filterType: selectedType.value.code,
    shiftType: selectedShiftType.value.code,
    showNotLogout: showNotLogout.value,
    startDate,
    endDate,
    page: 1,
    sort: getSortValues().sort,
    order: getSortValues().order
  });
  if (router.resolve(targetRoute).fullPath === route.fullPath) {
    currentPage.value = 1;
    await refreshData();
  } else {
    await router.push(targetRoute);
  }
};

// Function: Handle Error
const handleError = (error, context) => {
  console.error(`Error in ${context}:`, error);
  let errorMessage = 'An unexpected error occurred.';
  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  }
  toast.add({
    severity: 'error',
    summary: `Error: ${context}`,
    detail: errorMessage,
    life: 8000,
    group: 'br'
  });
};

// Computed: Check if Selected Rows Exist
const hasSelectedRows = computed(() => selectedRows.value.length > 0);
const hasSingleSelectedRow = computed(() => selectedRows.value.length === 1);
const hasMultipleSelectedRows = computed(() => selectedRows.value.length > 1);
const selectedAttendanceIds = computed(() => selectedRows.value
  .map(row => Number(row.attendance_id))
  .filter(attendanceId => Number.isSafeInteger(attendanceId) && attendanceId > 0));

const openAttendanceEditor = () => {
  if (!hasSingleSelectedRow.value) return;
  const attendance = selectedRows.value[0];
  const employeeId = Number(attendance.employee_id);
  const rosterId = Number(attendance.roster_id);
  const attendanceId = Number(attendance.attendance_id);
  if (![employeeId, rosterId, attendanceId].every(id => Number.isSafeInteger(id) && id > 0)) {
    return;
  }

  router.push({
    name: 'EditAttendance',
    params: { employeeId, rosterId, attendanceId },
    query: { returnTo: route.fullPath },
  });
};

const openAttendanceBulkEditor = () => {
  if (!hasMultipleSelectedRows.value || !authStore.isSuperAdmin) return;
  router.push({
    name: 'EditAttendanceBulk',
    params: { attendanceIds: selectedAttendanceIds.value.join(',') },
    query: { returnTo: route.fullPath },
  });
};

const openAttendanceCompanyEditor = () => {
  if (!hasMultipleSelectedRows.value) return;

  const attendanceIds = selectedRows.value
    .map(row => Number(row.attendance_id))
    .filter(attendanceId => Number.isSafeInteger(attendanceId) && attendanceId > 0);
  if (attendanceIds.length !== selectedRows.value.length) return;

  router.push({
    name: 'ChangeAttendanceCompany',
    params: { attendanceIds: attendanceIds.join(',') },
    query: { returnTo: route.fullPath }
  });
};

const scrollMainContentToTop = async () => {
  await nextTick();
  const scrollContainer = document.querySelector('[data-main-scroll-container]');
  if (scrollContainer) scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
  else window.scrollTo({ top: 0, behavior: 'smooth' });
};

const onPage = async (event) => {
  const parsedFilters = parseAttendanceRouteFilters(route.params);
  const parsedSort = parseAttendanceSortQuery(route.query);
  if (!parsedFilters.valid || !parsedSort.valid) {
    handleError(parsedFilters.error || parsedSort.error, 'Attendance URL');
    return;
  }

  const targetRoute = buildAttendanceRoute({
    companyId: parsedFilters.companyId,
    employeeIds: parsedFilters.employeeIds,
    filterType: parsedFilters.filterType,
    shiftType: parsedFilters.shiftType,
    showNotLogout: parsedFilters.showNotLogout,
    startDate: parsedFilters.startDate,
    endDate: parsedFilters.endDate,
    page: event.page + 1,
    sort: parsedSort.sort,
    order: parsedSort.order
  });
  selectedRows.value = [];
  await router.push(targetRoute);
  await scrollMainContentToTop();
};

const synchronizeRouteFilters = async () => {
  const parsedFilters = parseAttendanceRouteFilters(route.params);
  const parsedSort = parseAttendanceSortQuery(route.query);
  if (!parsedFilters.valid || !parsedSort.valid) {
    handleError(parsedFilters.error || parsedSort.error, 'Attendance URL');
    await router.replace(buildDefaultAttendanceRoute());
    return synchronizeRouteFilters();
  }

  const canonicalRoute = buildAttendanceRoute({
    companyId: parsedFilters.companyId,
    employeeIds: parsedFilters.employeeIds,
    filterType: parsedFilters.filterType,
    shiftType: parsedFilters.shiftType,
    showNotLogout: parsedFilters.showNotLogout,
    startDate: parsedFilters.startDate,
    endDate: parsedFilters.endDate,
    page: parsedFilters.page,
    sort: parsedSort.sort,
    order: parsedSort.order
  });
  if (router.resolve(canonicalRoute).fullPath !== route.fullPath) {
    await router.replace(canonicalRoute);
    return synchronizeRouteFilters();
  }

  const selectionResult = setRouteSelection(
    parsedFilters.companyId,
    parsedFilters.employeeIds
  );
  if (selectionResult.companyMissing || selectionResult.missingEmployeeIds.length) {
    const detail = selectionResult.companyMissing
      ? `Company ID is unavailable: ${parsedFilters.companyId}.`
      : `Employee IDs are unavailable for this company: ${selectionResult.missingEmployeeIds.join(', ')}.`;
    handleError(detail, 'Attendance URL');
    await router.replace(buildDefaultAttendanceRoute());
    return synchronizeRouteFilters();
  }

  selectedType.value = typeList.find(type => type.code === parsedFilters.typeCode)
    || typeList[0];
  selectedShiftType.value = shiftTypeList.find(
    type => type.code === parsedFilters.shiftTypeCode
  ) || shiftTypeList[0];
  showNotLogout.value = parsedFilters.showNotLogout;
  currentPage.value = parsedFilters.page;
  sortField.value = parsedSort.sort === 'attendance'
    ? 'attendance_checkin_unix'
    : 'shift_in_unix';
  sortOrder.value = parsedSort.sortOrder;
  dateRange.value = [parsedFilters.startCalendarDate, parsedFilters.endCalendarDate];
  const succeeded = await refreshData();

  if (succeeded && currentPage.value > attendancePagination.value.total_pages) {
    await router.replace(buildAttendanceRoute({
      companyId: parsedFilters.companyId,
      employeeIds: parsedFilters.employeeIds,
      filterType: parsedFilters.filterType,
      shiftType: parsedFilters.shiftType,
      showNotLogout: parsedFilters.showNotLogout,
      startDate: parsedFilters.startDate,
      endDate: parsedFilters.endDate,
      page: attendancePagination.value.total_pages,
      sort: parsedSort.sort,
      order: parsedSort.order
    }));
    return synchronizeRouteFilters();
  }
};

const onSort = async event => {
  const parsedFilters = parseAttendanceRouteFilters(route.params);
  if (!parsedFilters.valid) {
    handleError(parsedFilters.error, 'Attendance URL');
    return;
  }

  sortField.value = event.sortField === 'attendance_checkin_unix'
    ? 'attendance_checkin_unix'
    : 'shift_in_unix';
  sortOrder.value = event.sortOrder === -1 ? -1 : 1;
  const sortValues = getSortValues();
  await router.push(buildAttendanceRoute({
    companyId: parsedFilters.companyId,
    employeeIds: parsedFilters.employeeIds,
    filterType: parsedFilters.filterType,
    shiftType: parsedFilters.shiftType,
    showNotLogout: parsedFilters.showNotLogout,
    startDate: parsedFilters.startDate,
    endDate: parsedFilters.endDate,
    page: 1,
    sort: sortValues.sort,
    order: sortValues.order
  }));
};

// Watch: Handle Attendance Error
watch(attendanceError, (newAttendanceError) => {
  if (newAttendanceError) {
    handleError(newAttendanceError, 'Attendance');
  }
});

// Lifecycle Hook: Initialize Component
onMounted(async () => {
  try {
    await ensureSelectionData();
    await synchronizeRouteFilters();
    routeInitialized.value = true;
  } catch (error) {
    handleError(error, 'Component Initialization');
  }
});

watch(() => route.fullPath, () => {
  if (routeInitialized.value && route.name === 'ViewAttendanceFiltered') {
    synchronizeRouteFilters();
  }
});

// Add these computed properties after the other computed properties
const totalDuration = computed(() => attendanceSummary.value.roster_duration_minutes);
const totalWorkedHours = computed(() => attendanceSummary.value.worked_duration_minutes);
const totalNetWorkedHours = computed(() => attendanceSummary.value.net_worked_duration_minutes);

const sumSelectedDurationMinutes = (startField, endField) => {
  const totalMilliseconds = selectedRows.value.reduce((total, row) => {
    const start = Number(row[startField]);
    const end = Number(row[endField]);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= 0 || end < start) {
      return total;
    }
    return total + (end - start);
  }, 0);
  return Math.floor(totalMilliseconds / 60000);
};

const selectedRosterMinutes = computed(() => (
  sumSelectedDurationMinutes('shift_in_unix', 'shift_out_unix')
));

const selectedAttendanceMinutes = computed(() => (
  sumSelectedDurationMinutes('attendance_checkin_unix', 'attendance_checkout_unix')
));

const selectionSummary = computed(() => {
  const rowLabel = selectedRows.value.length === 1 ? 'Row' : 'Rows';
  return `${selectedRows.value.length} ${rowLabel} Selected with ${formatMinutesToTime(selectedAttendanceMinutes.value)} Attendance and ${formatMinutesToTime(selectedRosterMinutes.value)} Roster Hours.`;
});

// Helper function to convert minutes to HH:mm format
const formatMinutesToTime = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Add these computed properties
const calculateRowDurations = computed(() => (row) => {
  const shiftDuration = calculateDuration(row.shift_in_unix, row.shift_out_unix);
  const attendanceDuration = row.attendance_checkout_unix > 0 
    ? calculateDuration(row.attendance_checkin_unix, row.attendance_checkout_unix)
    : null;
  const netWorkedHours = row.attendance_checkout_unix > 0 
    ? calculateNetWorkedHours(
        row.shift_in_unix,
        row.shift_out_unix,
        row.attendance_checkin_unix,
        row.attendance_checkout_unix,
        row.attendance_status_in,
        row.attendance_status_out,
        row.over_below_status
      )
    : null;

  return {
    shiftDuration,
    attendanceDuration,
    netWorkedHours
  };
});
</script>

<style scoped>
.attendance-sticky-header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: minmax(0, 35%) minmax(0, 65%);
  align-items: center;
  padding-block: 0.5rem;
  box-shadow: 0 1px 0 var(--p-content-border-color);
}

.attendance-header-summary {
  min-width: 0;
}

.attendance-filters {
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.not-logout-filter {
  min-height: 2.25rem;
  white-space: nowrap;
  color: var(--p-text-muted-color);
  font-size: 0.875rem;
}

@media (max-width: 991px) {
  .attendance-sticky-header {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem;
  }

  .attendance-filters {
    justify-content: flex-start;
  }
}
</style>
